import type { FrontPiecesView, FrontRoomsView } from '@obraplan/core/application/contracts/takeoff';
import { progressFromRooms } from '@obraplan/core/domain/takeoff/room-progress';
import { frenteNaFila, type OutboxEntry } from '@/app/outbox-types';
import { syncDoCartao, type SyncDoCartao } from '@/features/planning/fila-da-conferencia';

/**
 * A execução por cômodo com a fila do aparelho por cima (Fase 43, etapa 4, RN-613, RN-614).
 *
 * Como no cartão da pessoa: o cômodo marcado sem sinal aparece pintado na planta, e a peça
 * marcada aparece executada, até a leitura do servidor chegar com o de verdade.
 */

/** As operações desta frente × serviço, na ordem em que foram feitas. */
export function filaDaFrente(
  entries: readonly OutboxEntry[],
  activityId: string,
  serviceId: string,
): OutboxEntry[] {
  const chave = frenteNaFila(activityId, serviceId);
  return entries.filter((entry) => entry.operation.resourceId === chave);
}

/** A marca da frente: "aguardando envio", ou o recusado com o motivo. */
export function syncDaExecucao(
  entries: readonly OutboxEntry[],
  activityId: string,
  serviceId: string,
): SyncDoCartao | undefined {
  return syncDoCartao(filaDaFrente(entries, activityId, serviceId));
}

/**
 * Os cômodos com o "não se aplica" e os percentuais da fila. O avanço geral é refeito com a mesma
 * conta do servidor (RN-445): a média pesada, sem os cômodos fora da conta.
 */
export function comodosComAFila(
  view: FrontRoomsView,
  entries: readonly OutboxEntry[],
  activityId: string,
  serviceId: string,
): FrontRoomsView {
  const doServico = filaDaFrente(entries, activityId, serviceId);
  if (!doServico.some((entry) => entry.operation.kind === 'comodos')) return view;

  let rooms = view.rooms;
  for (const { operation } of doServico) {
    if (operation.kind !== 'comodos') continue;
    const fora = new Map(operation.notApplicable.map((m) => [m.roomId, m.notApplicable]));
    const apontado = new Map(
      operation.progress.map((p) => [`${p.positionId}|${p.roomId}`, p.percent]),
    );
    rooms = rooms.map((room) => {
      const marca = fora.get(room.roomId);
      const percent = apontado.get(`${room.positionId}|${room.roomId}`);
      return {
        ...room,
        ...(marca === undefined ? {} : { notApplicable: marca }),
        ...(percent === undefined ? {} : { percent, recordedOn: operation.date }),
      };
    });
  }

  const avanco = progressFromRooms(
    rooms
      .filter((room) => !room.notApplicable)
      .map((room) => ({
        weight: room.weight,
        ...(room.percent === undefined ? {} : { percent: room.percent }),
      })),
  );
  const { progressFromRooms: _antes, ...semAvanco } = view;
  return { ...semAvanco, rooms, ...(avanco === undefined ? {} : { progressFromRooms: avanco }) };
}

/**
 * As peças com as mudanças da fila: a peça que saiu do dia dela volta a não executada; a que
 * entrou fica executada naquele dia.
 */
export function pecasComAFila(
  view: FrontPiecesView,
  entries: readonly OutboxEntry[],
  activityId: string,
  serviceId: string,
): FrontPiecesView {
  const doServico = filaDaFrente(entries, activityId, serviceId);
  if (!doServico.some((entry) => entry.operation.kind === 'pecas-da-frente')) return view;

  let pieces = view.pieces;
  for (const { operation } of doServico) {
    if (operation.kind !== 'pecas-da-frente') continue;
    for (const mudanca of operation.changes) {
      const entra = new Set(mudanca.add);
      const sai = new Set(mudanca.remove);
      pieces = pieces.map((piece) => {
        if (entra.has(piece.name)) return { ...piece, executedOn: mudanca.date };
        if (!sai.has(piece.name) || piece.executedOn !== mudanca.date) return piece;
        const { executedOn: _dia, ...semDia } = piece;
        return semDia;
      });
    }
  }
  return { ...view, pieces };
}
